import {
  Box,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Flex,
  Pagination,
} from '@mantine/core'
import { IconPlus, IconUsers } from '@tabler/icons-react'
import { Button } from 'components/Button'
import { useTranslation } from 'react-i18next'
import { FacultyCard } from './FacultyCard'
import type { ManagedFaculty } from '../types'

const GRID_BREAKPOINTS = [
  { maxWidth: 'md' as const, cols: 2 },
  { maxWidth: 'sm' as const, cols: 1 },
]

interface Props {
  faculty: ManagedFaculty[]
  loading: boolean
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  onEdit: (faculty: ManagedFaculty) => void
  onRemove: (faculty: ManagedFaculty) => void
  onAddFaculty: () => void
}

export function FacultyGrid({
  faculty,
  loading,
  page,
  totalPages,
  onPageChange,
  onEdit,
  onRemove,
  onAddFaculty,
}: Props) {
  const { t } = useTranslation()

  return (
    <>
      <Box mt='md'>
        {loading ? (
          <SimpleGrid
            cols={3}
            spacing='sm'
            breakpoints={GRID_BREAKPOINTS}
          >
            {Array.from({ length: 9 }, (_, i) => (
              <Skeleton
                key={i}
                height={160}
                radius='md'
                animate
              />
            ))}
          </SimpleGrid>
        ) : faculty.length === 0 ? (
          <Stack align='center' py='xl' spacing='md'>
            <IconUsers size={48} color='gray' stroke={1.5} />
            <Text color='dimmed'>
              {t('manage_faculty.empty')}
            </Text>
            <Button variant='light' leftIcon={<IconPlus size={16} />} onClick={onAddFaculty}>
              {t('manage_faculty.add_faculty')}
            </Button>
          </Stack>
        ) : (
          <SimpleGrid
            cols={3}
            spacing='sm'
            breakpoints={GRID_BREAKPOINTS}
          >
            {faculty.map(member => (
              <FacultyCard
                key={member.id}
                faculty={member}
                onEdit={onEdit}
                onRemove={onRemove}
              />
            ))}
          </SimpleGrid>
        )}
      </Box>

      {totalPages > 1 && (
        <Flex justify='center' mt='lg'>
          <Pagination
            value={page}
            onChange={onPageChange}
            total={totalPages}
            size='md'
          />
        </Flex>
      )}
    </>
  )
}
